import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  CircleDollarSign,
  Droplets,
  Scale,
  TrendingUp,
} from 'lucide-react';
import {
  BLENDING_COST_MODEL,
  computeBlendingTotals,
  computeVariance,
  PENDING_SETTLEMENT_TRIPS,
  resolveVendorName,
} from './mockFinancialSettlements';

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  border: '#CBD5E1',
  blue: '#3B82F6',
  blueHover: '#2563EB',
  blueTint: '#EFF6FF',
  text: '#1E293B',
  muted: '#64748B',
  earn: '#059669',
  warn: '#D97706',
  warnBg: '#FFFBEB',
  warnRing: '#FDE68A',
  earnBg: '#ECFDF5',
  earnRing: '#A7F3D0',
};


const lightCard = {
  backgroundColor: P.white,
  borderColor: P.grid,
};

function CircularIcon({ icon: Icon }) {
  return (
    <div
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-sm"
      style={{
        backgroundColor: P.blueTint,
        boxShadow: `inset 0 0 0 2px ${P.white}, 0 0 0 1px ${P.grid}`,
      }}
    >
      <Icon className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
    </div>
  );
}

function VarianceBadge({ variance, cleared }) {
  if (cleared || variance === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
        style={{
          backgroundColor: P.blueTint,
          color: P.earn,
          boxShadow: `inset 0 0 0 1px ${P.earnRing}`,
        }}
      >
        <CheckCircle2 className="h-3 w-3" />
        Cleared · 0 AED
      </span>
    );
  }

  const sign = variance > 0 ? '+' : '';
  return (
    <span
      className="inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
      style={{
        backgroundColor: P.warnBg,
        color: P.warn,
        boxShadow: `inset 0 0 0 1px ${P.warnRing}`,
      }}
    >
      Variance {sign}
      {variance.toLocaleString()} AED
    </span>
  );
}

function PayoutToast({ visible, tripId, onDismiss }) {
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(onDismiss, 4200);
    return () => clearTimeout(timer);
  }, [visible, onDismiss]);

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-6 right-6 z-50 max-w-sm animate-[fadeSlideIn_0.35s_ease-out]"
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg"
        style={{
          backgroundColor: P.blueTint,
          borderColor: P.blue,
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: P.blue }} />
        <div>
          <p className="text-sm font-extrabold" style={{ color: P.blue }}>
            Payout Approved
          </p>
          <p className="mt-0.5 text-xs font-semibold" style={{ color: P.muted }}>
            {tripId} cleared for finance disbursement pipeline
          </p>
        </div>
      </div>
    </div>
  );
}

function MetricChip({ label, value, icon: Icon }) {
  return (
    <div
      className="flex items-center gap-3 rounded-2xl border p-4 shadow-sm"
      style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
    >
      <CircularIcon icon={Icon} />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: P.muted }}>
          {label}
        </p>
        <p className="font-mono text-xl font-black tabular-nums" style={{ color: P.text }}>
          {value}
        </p>
      </div>
    </div>
  );
}

export default function FinancialSettlements() {
  const [trips, setTrips] = useState(() =>
    PENDING_SETTLEMENT_TRIPS.map((t) => ({ ...t })),
  );
  const [toast, setToast] = useState({ visible: false, tripId: '' });

  const blending = useMemo(() => computeBlendingTotals(BLENDING_COST_MODEL), []);

  const summary = useMemo(() => {
    const pending = trips.filter((t) => !t.payoutApproved).length;
    const withVariance = trips.filter(
      (t) => !t.payoutApproved && computeVariance(t.calculatedFreightAed, t.vendorInvoiceAed) !== 0,
    ).length;
    const totalFreight = trips.reduce((s, t) => s + t.calculatedFreightAed, 0);
    return { pending, withVariance, totalFreight };
  }, [trips]);

  const handleApprovePayout = (tripId) => {
    const trip = trips.find((t) => t.tripId === tripId);
    if (!trip || trip.payoutApproved) return;

    setTrips((prev) =>
      prev.map((t) =>
        t.tripId === tripId
          ? {
              ...t,
              payoutApproved: true,
              vendorInvoiceAed: t.calculatedFreightAed,
            }
          : t,
      ),
    );
    setToast({ visible: true, tripId });
  };

  const dismissToast = useCallback(() => setToast({ visible: false, tripId: '' }), []);

  return (
    <div className="w-full font-sans">
      <header className="mb-6">
        <span
          className="inline-block rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
          style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid', color: P.blue }}
        >
          Finance Control · Commercial Reconciliation
        </span>
        <h1 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl" style={{ color: P.text }}>
          Financial Settlements
        </h1>
        <p className="mt-1 text-xs font-bold uppercase tracking-wider" style={{ color: P.muted }}>
          Freight variance audit · Tank blending margin · Vendor payout clearance
        </p>
      </header>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricChip label="Pending Clearance" value={summary.pending} icon={Scale} />
        <MetricChip label="Variance Alerts" value={summary.withVariance} icon={CircleDollarSign} />
        <MetricChip
          label="Contract Freight (AED)"
          value={summary.totalFreight.toLocaleString()}
          icon={TrendingUp}
        />
      </div>

      <section
        className="mb-6 overflow-hidden rounded-2xl border shadow-sm"
        style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
      >
        <div
          className="flex items-center gap-2 border-b px-5 py-4"
          style={{
            backgroundColor: P.silver,
            borderColor: P.grid,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          }}
        >
          <CircularIcon icon={Scale} />
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: P.text }}>
              Commercial Reconciliation Ledger
            </h2>
            <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: P.muted }}>
              Completed trips pending finance clearance
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead>
              <tr
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: P.muted }}
              >
                <th className="border-b px-5 py-3" style={{ borderColor: P.grid }}>
                  Trip ID
                </th>
                <th className="border-b px-5 py-3" style={{ borderColor: P.grid }}>
                  Assigned Transporter
                </th>
                <th className="border-b px-5 py-3 text-right" style={{ borderColor: P.grid }}>
                  Calculated Freight
                </th>
                <th className="border-b px-5 py-3 text-right" style={{ borderColor: P.grid }}>
                  Vendor Invoice
                </th>
                <th className="border-b px-5 py-3" style={{ borderColor: P.grid }}>
                  Variance
                </th>
                <th className="border-b px-5 py-3 text-center" style={{ borderColor: P.grid }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {trips.map((trip) => {
                const variance = computeVariance(
                  trip.calculatedFreightAed,
                  trip.vendorInvoiceAed,
                );
                const cleared = trip.payoutApproved;

                return (
                  <tr
                    key={trip.tripId}
                    style={{ borderTop: `1px solid ${P.grid}`, backgroundColor: P.white }}
                  >
                    <td className="px-5 py-4 font-mono text-xs font-bold" style={{ color: P.blue }}>
                      {trip.tripId}
                    </td>
                    <td className="px-5 py-4 font-semibold" style={{ color: P.text }}>
                      {resolveVendorName(trip.vendorId)}
                      <span className="mt-0.5 block font-mono text-[10px]" style={{ color: P.muted }}>
                        {trip.vendorId}
                      </span>
                    </td>
                    <td
                      className="px-5 py-4 text-right font-mono font-bold tabular-nums"
                      style={{ color: P.text }}
                    >
                      {trip.calculatedFreightAed.toLocaleString()} AED
                    </td>
                    <td
                      className="px-5 py-4 text-right font-mono font-bold tabular-nums"
                      style={{ color: P.muted }}
                    >
                      {trip.vendorInvoiceAed.toLocaleString()} AED
                    </td>
                    <td className="px-5 py-4">
                      <VarianceBadge variance={variance} cleared={cleared} />
                    </td>
                    <td className="px-5 py-4 text-center">
                      {!cleared ? (
                        <button
                          type="button"
                          onClick={() => handleApprovePayout(trip.tripId)}
                          className="rounded-lg px-3 py-2 text-[10px] font-black uppercase tracking-wide text-white shadow-sm transition"
                          style={{ backgroundColor: P.blue }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = P.blueHover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = P.blue;
                          }}
                        >
                          Approve for Payout
                        </button>
                      ) : (
                        <span
                          className="text-[10px] font-bold uppercase"
                          style={{ color: P.earn }}
                        >
                          Approved ✓
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
      >
        <div
          className="border-b px-5 py-4"
          style={{
            backgroundColor: P.silver,
            borderColor: P.grid,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          }}
        >
          <div className="flex items-center gap-2">
            <CircularIcon icon={Droplets} />
            <div>
              <h2 className="text-sm font-extrabold" style={{ color: P.text }}>
                Tank Blending Logistics Cost Control
              </h2>
              <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: P.muted }}>
                Terminal blending feasibility snapshot
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-2">
          <div
            className="rounded-xl border p-5"
            style={{ borderColor: P.grid, backgroundColor: P.silver }}
          >
            <p className="mb-4 text-[10px] font-bold uppercase tracking-widest" style={{ color: P.muted }}>
              Cost Build-Up (AED / Metric Ton)
            </p>
            <div className="space-y-3">
              {[
                { label: 'Base Product Cost', value: BLENDING_COST_MODEL.baseProductCostPerMt },
                {
                  label: 'Terminal Blending Execution Fee',
                  value: BLENDING_COST_MODEL.terminalBlendingFeePerMt,
                },
                {
                  label: 'Transport Freight Cost',
                  value: BLENDING_COST_MODEL.transportFreightPerMt,
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between rounded-lg border px-3 py-2.5"
                  style={{ backgroundColor: P.white, borderColor: P.grid }}
                >
                  <span className="text-xs font-semibold" style={{ color: P.muted }}>
                    {row.label}
                  </span>
                  <span className="font-mono text-sm font-bold tabular-nums" style={{ color: P.text }}>
                    {row.value.toLocaleString()} AED
                  </span>
                </div>
              ))}

              <div className="flex items-center justify-center py-1" style={{ color: P.blue }}>
                <ArrowRight className="h-4 w-4 rotate-90" />
              </div>

              <div
                className="flex items-center justify-between rounded-xl border-2 px-4 py-3"
                style={{
                  backgroundColor: P.blueTint,
                  borderColor: P.blue,
                  borderStyle: 'solid',
                }}
              >
                <span className="text-xs font-extrabold uppercase" style={{ color: P.blue }}>
                  Total Delivered Cost / MT
                </span>
                <span className="font-mono text-lg font-black tabular-nums" style={{ color: P.text }}>
                  {blending.totalDeliveredCost.toLocaleString()} AED
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex flex-col justify-between rounded-xl border p-5"
            style={{ borderColor: P.grid, backgroundColor: P.white }}
          >
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: P.muted }}>
                Net Margin vs UAE Market Trading Price
              </p>
              <div
                className="mt-4 flex items-center gap-4 rounded-2xl border-2 border-dashed p-6 text-center"
                style={{ borderColor: P.border, backgroundColor: P.silver }}
              >
                <div
                  className="mx-auto flex h-20 w-20 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: P.blueTint,
                    boxShadow: `inset 0 0 0 3px ${P.white}, 0 0 0 1px ${P.grid}`,
                  }}
                >
                  <span className="font-mono text-xl font-black" style={{ color: P.blue }}>
                    {blending.marginPct}%
                  </span>
                </div>
              </div>
              <dl className="mt-4 space-y-2 text-xs">
                <div className="flex justify-between font-semibold" style={{ color: P.muted }}>
                  <dt>UAE Market Trading Price</dt>
                  <dd className="font-mono font-bold" style={{ color: P.text }}>
                    {BLENDING_COST_MODEL.uaeMarketTradingPricePerMt.toLocaleString()} AED / MT
                  </dd>
                </div>
                <div className="flex justify-between font-semibold" style={{ color: P.muted }}>
                  <dt>Total Delivered Cost</dt>
                  <dd className="font-mono font-bold" style={{ color: P.text }}>
                    {blending.totalDeliveredCost.toLocaleString()} AED / MT
                  </dd>
                </div>
                <div
                  className="flex justify-between border-t pt-2 font-bold"
                  style={{ borderColor: P.grid, color: P.earn }}
                >
                  <dt>Net Margin</dt>
                  <dd className="font-mono text-base">
                    +{blending.netMargin.toLocaleString()} AED / MT
                  </dd>
                </div>
              </dl>
            </div>
            <p className="mt-4 text-[10px] font-medium leading-relaxed" style={{ color: P.muted }}>
              Read-only KPI · Benchmarked against current UAE bulk fuel trading desk reference
              curve (Jun 2026).
            </p>
          </div>
        </div>
      </section>

      <PayoutToast
        visible={toast.visible}
        tripId={toast.tripId}
        onDismiss={dismissToast}
      />
    </div>
  );
}
